import { useEffect, useRef } from 'react'
import { MemberProposal } from '../proposedMember'
import useMemberProposal from './useMemberProposal'

interface Params {
  // Creation only: an existing project is never reassigned behind one's back.
  enabled: boolean
  circleId: string | undefined
  memberId: string | null | undefined
  // True once the person picked or cleared the member by hand.
  isMemberDirty: boolean
  setMemberId(memberId: string | null): void
}

interface Result {
  proposal: MemberProposal | undefined
  // The member shown is the one this hook proposed.
  isProposed: boolean
}

// #123: fills the member of a new project when its role has a single holder.
// It only ever overwrites an empty field or its own earlier proposal — never a
// member someone chose, nor a default the screen passed (the dashboard's
// "my projects" keeps assigning to oneself).
export default function useAutoProposeMember({
  enabled,
  circleId,
  memberId,
  isMemberDirty,
  setMemberId,
}: Params): Result {
  const proposal = useMemberProposal(enabled ? circleId : undefined)
  const lastProposedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled || isMemberDirty) return
    const current = memberId ?? null
    if (current !== null && current !== lastProposedRef.current) return
    const next = proposal?.kind === 'single' ? proposal.memberId : null
    lastProposedRef.current = next
    if (next !== current) setMemberId(next)
  }, [enabled, proposal])

  return {
    proposal,
    isProposed: !!memberId && memberId === lastProposedRef.current,
  }
}
